import { useState, useEffect } from "react";
import { BASE_URL } from "../components/BaseUrl";

/**
 * Convert image URL to base64 for PDF generation
 * @param {string} url - Image URL
 * @returns {Promise<string|null>} - Base64 string or null
 */
const imageUrlToBase64 = async (url) => {
  if (!url) return null;
  
  try {
    // If URL is already base64, return as is
    if (url.startsWith("data:image")) {
      return url;
    }

    // Fix malformed URLs
    let imageUrl = url;
    if (imageUrl.startsWith("/uploads/logos/https")) {
      imageUrl = imageUrl.replace("/uploads/logos/", "");
    }
    
    // Convert relative URL to absolute if needed
    if (!imageUrl.startsWith("http")) {
      imageUrl = `${BASE_URL}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;
    }
    
    console.log("🖼️ Fetching logo from:", imageUrl);
    
    const response = await fetch(imageUrl, {
      method: "GET",
      mode: "cors",
    });

    if (!response.ok) {
      console.warn("⚠️ Failed to fetch company logo:", imageUrl, response.status);
      return null;
    }

    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log("✅ Logo converted to base64, length:", reader.result?.length);
        resolve(reader.result);
      };
      reader.onerror = (err) => {
        console.warn("⚠️ Error reading logo blob:", err);
        reject(err);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("⚠️ Error converting logo to base64:", error);
    return null;
  }
};

/**
 * Hook to fetch company admin profile for workers
 * Uses the worker token to get company details (logo, name, address, etc.)
 * @param {string} token - Decrypted worker token
 * @returns {object} - { companyData, loading, error }
 */
export const useWorkerCompanyProfile = (token) => {
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchCompanyProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${BASE_URL}/workers/profile/company-admin`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!response.ok || data.error) {
          throw new Error(data.message || "Failed to fetch company profile");
        }

        if (data.data?.company_admin) {
          const admin = data.data.company_admin;
          
          // Convert logo to base64 for PDF generation
          let logoBase64 = null;
          if (admin.company?.logo) {
            // Fix malformed logo URL first
            let logoUrl = admin.company.logo;
            if (logoUrl.startsWith("/uploads/logos/https")) {
              logoUrl = logoUrl.replace("/uploads/logos/", "");
            }
            // If it's already a full URL, use it; otherwise try to convert
            if (logoUrl.startsWith("http")) {
              logoBase64 = await imageUrlToBase64(logoUrl);
            } else if (logoUrl.startsWith("data:")) {
              // Already base64
              logoBase64 = logoUrl;
            } else {
              // Try to fetch as relative URL
              logoBase64 = await imageUrlToBase64(logoUrl);
            }
          }

          setCompanyData({
            companyName: admin.company?.name || admin.company?.legal_name || "",
            companyAddress: admin.company?.address || "",
            companyEmail: admin.company?.email || "",
            companyPhone: admin.company?.phone || "",
            companyLogo: logoBase64 || "",
            authorizedSignatory: admin.name || "",
            companyContact: admin.company?.email || admin.email || "",
          });
        }
      } catch (err) {
        console.error("❌ Error fetching company profile:", err);
        setError(err.message);
        // Set default values on error
        setCompanyData({
          companyName: "",
          companyAddress: "",
          companyEmail: "",
          companyPhone: "",
          companyLogo: "",
          authorizedSignatory: "",
          companyContact: "",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyProfile();
  }, [token]);

  return { companyData, loading, error };
};
